import { dispatchNetworkEvent } from './event-bus';
import { applyOfflineConfig, getOfflineInstance, isOffline } from './offline';

let started = false;
let lastConnectionDetail = null;
let hasPendingConnectionChange = false;
let offlineListenersInitialized = false;
let browserListenersInitialized = false;
let lastOnlineStatus = null;

const syncInitialOnlineStatus = () => {
	if (lastOnlineStatus !== null) return;

	lastOnlineStatus = !isOffline();
};

const getConnection = () => {
	if (typeof navigator === 'undefined') return null;

	return navigator.connection
		|| navigator.mozConnection
		|| navigator.webkitConnection
		|| null;
};

const isDocumentHidden = () => {
	if (typeof document === 'undefined' || typeof document.visibilityState !== 'string') return false;

	return document.visibilityState === 'hidden';
};

const getConnectionDetail = connection => {
	if (!connection) return null;

	return {
		type:     connection.effectiveType,
		downlink: connection.downlink,
		rtt:      connection.rtt,
		saveData: connection.saveData
	};
};

const hasDetailChanged = (prev, next) => {
	if (!prev || !next) return true;

	return prev.type !== next.type
		|| prev.downlink !== next.downlink
		|| prev.rtt !== next.rtt
		|| prev.saveData !== next.saveData;
};

const emitConnectionEvent = detail => {
	if (!detail || !hasDetailChanged(lastConnectionDetail, detail)) return;

	lastConnectionDetail = detail;
	dispatchNetworkEvent('epic:network:connection_change', detail);
};

const emitOfflineEvent = (detail = {}) => {
	if (lastOnlineStatus === false) return;

	lastOnlineStatus = false;
	dispatchNetworkEvent('epic:network:offline', detail);
};

const emitOnlineEvent = (detail = {}) => {
	if (lastOnlineStatus === true) return;

	lastOnlineStatus = true;
	dispatchNetworkEvent('epic:network:online', detail);
};

const emitOfflineFromLibrary = (detail = {}) => {
	emitOfflineEvent({
		source: 'offline-js',
		...detail
	});
};

const emitOnlineFromLibrary = (detail = {}) => {
	emitOnlineEvent({
		source: 'offline-js',
		...detail
	});
};

const setupOfflineListeners = () => {
	const offline = getOfflineInstance();

	if (!offline) return false;

	applyOfflineConfig();

	const notifyOffline = () => emitOfflineFromLibrary({ state: offline.state });
	const notifyOnline = () => emitOnlineFromLibrary({ state: offline.state });

	if (!offlineListenersInitialized) {
		if (typeof offline.on === 'function') {
			offline.on('down', notifyOffline);
			offline.on('confirmed-down', notifyOffline);
			offline.on('up', notifyOnline);
			offline.on('confirmed-up', notifyOnline);
			offline.on('reconnect:connecting', () => {
				dispatchNetworkEvent('epic:network:unstable', {
					source: 'offline-js',
					state:  offline.state,
					phase:  'reconnect'
				});
			});
		}

		offlineListenersInitialized = true;
	}

	if (typeof offline.check === 'function') offline.check();

	if (isOffline()) notifyOffline();

	return true;
};

const setupOnlineOfflineListeners = () => {
	if (browserListenersInitialized || typeof window === 'undefined') return;

	browserListenersInitialized = true;

	window.addEventListener('online', () => {
		emitOnlineEvent({
			source: 'browser'
		});
	});

	window.addEventListener('offline', () => {
		emitOfflineEvent({
			source: 'browser'
		});
	});
};

const setupConnectionListener = () => {
	const connection = getConnection();

	if (!connection) return;

	const emitConnectionChange = () => {
		const detail = getConnectionDetail(connection);

		if (!detail) return;

		if (isDocumentHidden()) {
			hasPendingConnectionChange = true;
			return;
		}

		hasPendingConnectionChange = false;
		emitConnectionEvent(detail);
	};

	const setupVisibilityListener = () => {
		if (typeof document === 'undefined'
			|| typeof document.addEventListener !== 'function'
		) return;

		document.addEventListener('visibilitychange', () => {
			if (!hasPendingConnectionChange || isDocumentHidden()) return;

			hasPendingConnectionChange = false;
			emitConnectionChange();
		});
	};

	if (typeof connection.addEventListener === 'function') {
		connection.addEventListener('change', () => {
			emitConnectionChange();
		});
	}

	else if ('onchange' in connection) {
		connection.onchange = () => {
			emitConnectionChange();
		};
	}

	setupVisibilityListener();
	emitConnectionChange();
};

const startNetworkMonitor = () => {
	if (started) return;

	started = true;

	setupOfflineListeners();
	syncInitialOnlineStatus();
	setupOnlineOfflineListeners();

	setupConnectionListener();
};

export { startNetworkMonitor };
